import React from 'react';
import { Link } from 'react-router-dom';
import { Card, Button, Spinner, Alert, Tooltip } from '@dopomogai/ui';
import { MessageSquare } from 'lucide-react';
import { useConversations } from '@dopomogai/agent-kit';
import { ConversationListItem } from '../conversations/ConversationListItem';

interface RecentConversationsCardProps {
  agentId: string;
}

export const RecentConversationsCard: React.FC<RecentConversationsCardProps> = ({ agentId }) => {
  const { conversations, isLoading, error } = useConversations(agentId);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-40">
          <Spinner text="Loading conversations..." />
        </div>
      );
    }

    if (error) {
      return (
        <div className="p-4">
          <Alert type="error" title="Could not load conversations" message={error.message} />
        </div>
      );
    }

    if (!conversations || conversations.length === 0) {
      return (
        <div className="text-center py-10 px-4">
          <p className="text-sm text-slate-500">
            No conversations have been recorded for this agent yet.
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-2 p-1">
        {conversations.slice(0, 5).map(conv => (
          <ConversationListItem
            key={conv.id}
            conversation={conv}
            detailPageUrl={`/app/conversations/${conv.id}`}
          />
        ))}
      </div>
    );
  };

  return (
    <Card
      title="Recent Conversations"
      icon={<MessageSquare size={18} />}
      headerBgColor="bg-slate-100"
      actionButton={
        <Tooltip text="View all conversations for this agent">
          <Link to={`/app/agents/${agentId}/conversations`} aria-label="View all conversations">
            <Button variant="secondary" size="sm" text="View All" />
          </Link>
        </Tooltip>
      }
    >
      {renderContent()}
    </Card>
  );
};